import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { SectionTitle, Spinner, StatusPill } from "@/components/common";
import { PlanCards } from "@/pages/Pricing";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";

function Payouts() {
  const [sp] = useSearchParams();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/payouts/status").then((r) => setC(r.data.connect || {})); }, [sp]);
  const go = async () => {
    setBusy(true);
    try { const { data } = await api.post("/payouts/onboard", { origin_url: window.location.origin }); window.location.href = data.url; } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  if (!c) return null;
  return (
    <div className="card flex flex-wrap items-center justify-between gap-4 p-5" data-testid="payouts-card">
      <div><p className="font-semibold">Get paid by card</p>
        <p className="text-sm text-slate-600">{c.charges_enabled ? "Payouts are active. Turn on card payments per trip in Trip settings." : c.account_id ? "Finish verifying your details with Stripe to receive payouts." : "Let travelers pay their balance by card, straight to your bank. Stripe verifies your identity."}</p></div>
      {!c.charges_enabled && <Button className="rounded-full" onClick={go} disabled={busy} data-testid="payouts-onboard-button">{busy ? "Opening Stripe…" : c.account_id ? "Continue setup" : "Set up payouts"}</Button>}
      {c.charges_enabled && <StatusPill status="paid" label="Payouts active" testid="payouts-active" />}
    </div>
  );
}

export default function Billing() {
  const { refreshUser } = useAuth();
  const [plans, setPlans] = useState(null);
  const [me, setMe] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/billing/me").then((r) => setMe(r.data));
  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data)); load(); }, []);
  if (!plans || !me) return <Spinner />;
  const sub = me.subscription;
  const active = sub && ["active", "trialing", "past_due"].includes(sub.status);
  const run = async (key, fn) => {
    setBusy(key);
    try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const checkout = (plan) => run(plan, async () => {
    const { data } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  });
  const change = (plan) => run(plan, async () => { const { data } = await api.post("/billing/change-plan", { plan }); setMe(data); await refreshUser(); toast.success("Plan updated"); });
  const askCancel = () => run("cancel", async () => { const { data } = await api.get("/billing/downgrade-preview?target=free"); setPreview(data); });
  const cancel = () => run("cancel", async () => { const { data } = await api.post("/billing/cancel"); setMe(data); setPreview(null); toast.success("Your plan will end at the close of this billing period"); });
  const resume = () => run("resume", async () => { const { data } = await api.post("/billing/resume"); setMe(data); toast.success("Subscription resumed"); });

  const action = (k) => {
    if (k === me.plan) return <p className="rounded-full bg-slate-100 py-2.5 text-center text-sm font-semibold" data-testid={`current-plan-${k}`}>Current plan</p>;
    if (k === "free") return active && !sub.cancel_at_period_end ? <Button variant="outline" className="w-full rounded-full" onClick={askCancel} disabled={!!busy} data-testid="downgrade-free-button">Downgrade to Free</Button> : null;
    return <Button className="w-full rounded-full" disabled={!!busy} onClick={() => (active ? change(k) : checkout(k))} data-testid={`upgrade-${k}-button`}>{busy === k ? "Please wait…" : active ? `Switch to ${plans.plans[k].name}` : `Upgrade to ${plans.plans[k].name}`}</Button>;
  };

  return (
    <div className="space-y-6">
      <SectionTitle title="Plan & billing" sub="Plans apply to trips you own. Travelers on your trips never need to pay." />
      {me.mode === "test" && <p className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900" data-testid="billing-test-mode">Test mode: no real charges. Use card 4242 4242 4242 4242, any future date and any CVC.</p>}
      {active && (
        <div className="card flex flex-wrap items-center justify-between gap-4 p-5" data-testid="subscription-status">
          <div>
            <p className="font-semibold">{plans.plans[sub.plan]?.name} subscription <StatusPill status={sub.cancel_at_period_end ? "overdue" : "paid"} label={sub.cancel_at_period_end ? "Ends soon" : sub.status} /></p>
            <p className="text-sm text-slate-600">{sub.cancel_at_period_end ? "Ends" : "Renews"} {fmtDate(sub.current_period_end)}</p>
          </div>
          {sub.cancel_at_period_end && <Button variant="outline" className="rounded-full" onClick={resume} data-testid="resume-subscription-button">Keep my plan</Button>}
        </div>
      )}
      <PlanCards plans={plans.plans} labels={plans.feature_labels} current={me.plan} action={action} />
      <Payouts />
      <AlertDialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <AlertDialogContent data-testid="downgrade-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Before you downgrade</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-700">
                <p>Your plan stays active until {fmtDate(sub?.current_period_end)}. After that:</p>
                <div><p className="font-semibold">You keep</p><ul className="list-disc pl-5">{preview?.kept.map((k) => <li key={k}>{k}</li>)}</ul></div>
                {preview?.features_lost.length > 0 && <div><p className="font-semibold">You lose access to</p><ul className="list-disc pl-5">{preview.features_lost.map((k) => <li key={k}>{k}</li>)}</ul></div>}
                {preview?.trip_impacts.length > 0 && <div><p className="font-semibold">Trip limits</p><ul className="list-disc pl-5">{preview.trip_impacts.map((k) => <li key={k}>{k}</li>)}</ul></div>}
                {preview?.paused?.length > 0 && <div><p className="font-semibold">Paused</p><ul className="list-disc pl-5">{preview.paused.map((k) => <li key={k}>{k}</li>)}</ul></div>}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="downgrade-cancel">Keep plan</AlertDialogCancel>
            <AlertDialogAction onClick={cancel} data-testid="downgrade-confirm">Downgrade at period end</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
